/**
 * Strips script-execution vectors out of server-supplied report HTML while
 * leaving presentation intact.
 *
 * The avis / receipt / declaration / company templates are assigned to a div
 * that is then attached to document.body so html2canvas can rasterise it. That
 * makes the markup live: an `onerror` or `onload` handler inside a template
 * runs in the signed-in user's session, and templates are authored through
 * template-manager, so a template author could otherwise reach a reviewing
 * officer's session.
 *
 * Angular's DomSanitizer is deliberately not used here. Its allowlist has no
 * `style` attribute and no `<style>` element, so sanitizing through it strips
 * the styling these PDFs are laid out with. This scrub keeps `style`, `<style>`
 * and image `src` (logos, QR codes) and removes only what can execute.
 *
 * DOMParser builds an inert document: scripts never run and images are never
 * fetched, so nothing can fire before the scrub happens.
 */

const FORBIDDEN_ELEMENTS = [
  'script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'base', 'link', 'meta'
];

const URL_ATTRIBUTES = [
  'action', 'background', 'cite', 'data', 'formaction', 'href', 'longdesc',
  'poster', 'src', 'srcset', 'xlink:href'
];

const SAFE_SCHEME = /^(?:https?|mailto|tel):/i;

// SVG delivered through <img>/CSS is rendered in a non-scripting mode, and the
// elements that would give it script access (object, embed, iframe) are removed
// above, so image data URIs are safe to keep. QR codes arrive this way.
const SAFE_DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp|svg\+xml)[;,]/i;

function isSafeUrl(value: string): boolean {
  // Control characters are stripped before testing so that `java\tscript:` and
  // friends cannot smuggle a scheme past the check.
  const url = value.replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
  if (url === '') {
    return true;
  }
  // No scheme means the URL is relative, and therefore same-origin.
  if (!/^[a-z][a-z0-9+.\-]*:/i.test(url)) {
    return true;
  }
  return SAFE_SCHEME.test(url) || SAFE_DATA_IMAGE.test(url);
}

// srcset is a comma-separated candidate list, each entry a URL plus an optional
// descriptor. It is kept only if every candidate is safe.
function isSafeSrcset(value: string): boolean {
  return value
    .split(',')
    .map(candidate => candidate.trim().split(/\s+/)[0])
    .filter(url => url !== '')
    .every(isSafeUrl);
}

function isSafeAttributeValue(name: string, value: string): boolean {
  return name === 'srcset' ? isSafeSrcset(value) : isSafeUrl(value);
}

export function sanitizeReportHtml(html: unknown): string {
  if (html === null || html === undefined) {
    return '';
  }
  const source = typeof html === 'string' ? html : String(html);
  if (source.trim() === '') {
    return '';
  }

  const doc = new DOMParser().parseFromString(source, 'text/html');

  doc.querySelectorAll(FORBIDDEN_ELEMENTS.join(',')).forEach(el => el.remove());

  doc.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if (URL_ATTRIBUTES.indexOf(name) !== -1 && !isSafeAttributeValue(name, attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // When the template is a full document, the parser puts <style> in <head>.
  // Assigning the raw string to innerHTML used to flatten head content into the
  // container, so those rules have to be carried over or the PDF loses its
  // layout.
  const headStyles = Array.from(doc.head.querySelectorAll('style'))
    .map(style => style.outerHTML)
    .join('');

  return headStyles + doc.body.innerHTML;
}
